import React, { Suspense } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";
import GuestOnlyRoute from "./components/GuestOnlyRoute";
import Loader from "./components/ui/Loader";

// Import pages
const FarmlandRentCalculator = React.lazy(() =>
  import("./pages/farmland-rent-calculator")
);
const LandingPage = React.lazy(() => import("./pages/landing-page"));
const AgriculturalAILanding = React.lazy(() =>
  import("./pages/agricultural-ai-landing")
);
const MarketTrendAlertsDashboard = React.lazy(() =>
  import("./pages/market-trend-alerts-dashboard")
);
const ResultsDashboard = React.lazy(() => import("./pages/results-dashboard"));
const ProfessionalResourcesDirectory = React.lazy(() =>
  import("./pages/professional-resources-directory")
);
const GPSFieldMappingInterface = React.lazy(() =>
  import("./pages/gps-field-mapping-interface")
);
const AIScenarioSimulatorDashboard = React.lazy(() =>
  import("./pages/ai-scenario-simulator-dashboard")
);
const CalculationHistoryDashboard = React.lazy(() =>
  import("./pages/calculation-history-dashboard")
);
const PricingPlansPage = React.lazy(() => import("./pages/pricing-plans-page"));
const EnhancedSubscriptionManagementPortal = React.lazy(() =>
  import("./pages/enhanced-subscription-management-portal")
);
const WeatherRiskAssessmentDashboard = React.lazy(() =>
  import("./pages/weather-risk-assessment-dashboard")
);
const InsightsHub = React.lazy(() => import("./pages/insights-hub"));
const BlogContentPages = React.lazy(() => import("./pages/blog-content-pages"));
const EnhancedSiteFooter = React.lazy(() =>
  import("./pages/enhanced-site-footer")
);
const EnterpriseAPIManagementPortal = React.lazy(() =>
  import("./pages/enterprise-api-management-portal")
);
const SignInPage = React.lazy(() => import("./pages/sign-in-page"));
const SignUpPage = React.lazy(() => import("./pages/sign-up-page"));
const SubscriptionManagementPortal = React.lazy(() =>
  import("./pages/subscription-management-portal")
);
const EnhancedFarmlandRentCalculator = React.lazy(() =>
  import("./pages/enhanced-farmland-rent-calculator")
);
const LeaseNegotiationGuidancePortal = React.lazy(() =>
  import("./pages/lease-negotiation-guidance-portal")
);
const InsurancePremiumCalculator = React.lazy(() =>
  import("./pages/insurance-premium-calculator")
);
const LandRentalAgreementTemplateGenerator = React.lazy(() =>
  import("./pages/land-rental-agreement-template-generator")
);
const PdfExportService = React.lazy(() => import("./pages/pdf-export-service"));
const SubscriptionSuccessPage = React.lazy(() =>
  import("./pages/subscription-success")
);
const AIAgriculturalAssistantChat = React.lazy(() =>
  import("./pages/ai-agricultural-assistant-chat")
);
const AIAgriculturalAssistantChatV2 = React.lazy(() =>
  import("./pages/ai-agricultural-assistant-chat-v2")
);
const AIAgriculturalAssistantChatV3 = React.lazy(() =>
  import("./pages/ai-agricultural-assistant-chat-v3")
);
const AIAgriculturalAssistantChatV4 = React.lazy(() =>
  import("./pages/ai-agricultural-assistant-chat-v4")
);
const LocationPage = React.lazy(() => import("./pages/location"));
const BlogIndex = React.lazy(() => import("./pages/blog"));
const BlogPostPage = React.lazy(() => import("./pages/blog/BlogPost"));
const GrantsIndex = React.lazy(() => import("./pages/grants"));
const GrantDetailPage = React.lazy(() => import("./pages/grants/GrantDetail"));
const GuidesIndex = React.lazy(() => import("./pages/guides"));
const GuideDetailPage = React.lazy(() => import("./pages/guides/GuideDetail"));
const CropsIndex = React.lazy(() => import("./pages/crops"));
const CropDetailPage = React.lazy(() => import("./pages/crops/CropDetail"));
const StatePage = React.lazy(() => import("./pages/state"));
const AgriculturalLandRentalRates2025 = React.lazy(() => import("./pages/agricultural-land-rental-rates-2025"));
import NotFound from "./pages/NotFound";
const LegalPage = React.lazy(() => import("./pages/legal"));
const TryCalculator = React.lazy(() => import("./pages/try-calculator"));
const TryGrants = React.lazy(() => import("./pages/try-grants"));
const TryAIAdvisor = React.lazy(() => import("./pages/try-ai-advisor"));

// SEO Pages
import FarmlandLeasingGuide from "./pages/seo/FarmlandLeasingGuide";
import AgriculturalRentalRates from "./pages/seo/AgriculturalRentalRates";
const LongTailContent = React.lazy(() => import("./pages/seo/LongTailContent"));
const LearningCenter = React.lazy(() => import("./pages/seo/LongTailContent").then(m => ({ default: m.LearningCenter })));

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <Suspense fallback={<Loader />}>
          <RouterRoutes>
            {/* Landing Page */}
            <Route path="/landing-page" element={<LandingPage />} />
            <Route path="/landing" element={<AgriculturalAILanding />} />

            {/* Main Application */}
            <Route path="/" element={<FarmlandRentCalculator />} />
            <Route
              path="/market-trend-alerts-dashboard"
              element={<MarketTrendAlertsDashboard />}
            />
            <Route path="/results-dashboard" element={<ResultsDashboard />} />
            <Route
              path="/professional-resources-directory"
              element={<ProfessionalResourcesDirectory />}
            />
            <Route
              path="/gps-field-mapping-interface"
              element={<GPSFieldMappingInterface />}
            />
            <Route
              path="/ai-scenario-simulator-dashboard"
              element={<AIScenarioSimulatorDashboard />}
            />
            <Route
              path="/calculation-history-dashboard"
              element={<CalculationHistoryDashboard />}
            />
            <Route path="/pricing-plans-page" element={<PricingPlansPage />} />
            <Route
              path="/enhanced-subscription-management-portal"
              element={<EnhancedSubscriptionManagementPortal />}
            />
            <Route
              path="/weather-risk-assessment-dashboard"
              element={<WeatherRiskAssessmentDashboard />}
            />
            <Route path="/insights-hub" element={<InsightsHub />} />
            <Route path="/blog-content-pages" element={<BlogContentPages />} />
            <Route
              path="/enhanced-site-footer"
              element={<EnhancedSiteFooter />}
            />
            <Route
              path="/enterprise-api-management-portal"
              element={<EnterpriseAPIManagementPortal />}
            />
            <Route
              path="/enhanced-farmland-rent-calculator"
              element={<EnhancedFarmlandRentCalculator />}
            />
            <Route
              path="/lease-negotiation-guidance-portal"
              element={<LeaseNegotiationGuidancePortal />}
            />
            <Route
              path="/insurance-premium-calculator"
              element={<InsurancePremiumCalculator />}
            />
            <Route
              path="/land-rental-agreement-template-generator"
              element={<LandRentalAgreementTemplateGenerator />}
            />
            <Route path="/pdf-export-service" element={<PdfExportService />} />

            {/* AI Features - All Versions */}
            <Route
              path="/ai-agricultural-assistant-chat"
              element={<AIAgriculturalAssistantChat />}
            />
            <Route
              path="/ai-agricultural-assistant-chat-v2"
              element={<AIAgriculturalAssistantChatV2 />}
            />
            <Route
              path="/ai-agricultural-assistant-chat-v3"
              element={<AIAgriculturalAssistantChatV3 />}
            />
            <Route
              path="/ai-agricultural-assistant-chat-v4"
              element={<AIAgriculturalAssistantChatV4 />}
            />

            {/* Authentication Routes - Protected by GuestOnlyRoute */}
            <Route
              path="/sign-in"
              element={
                <GuestOnlyRoute>
                  <SignInPage />
                </GuestOnlyRoute>
              }
            />
            <Route
              path="/sign-up"
              element={
                <GuestOnlyRoute>
                  <SignUpPage />
                </GuestOnlyRoute>
              }
            />

            {/* Subscription Routes */}
            <Route
              path="/subscription-management-portal"
              element={<SubscriptionManagementPortal />}
            />
            <Route
              path="/subscription-success"
              element={<SubscriptionSuccessPage />}
            />

            {/* SEO Routes */}
            <Route
              path="/guides/farmland-leasing"
              element={<FarmlandLeasingGuide />}
            />
            <Route
              path="/data/agricultural-rental-rates"
              element={<AgriculturalRentalRates />}
            />

            {/* Dynamic Location Route */}
            <Route path="/calculator/:slug" element={<LocationPage />} />

            {/* Blog */}
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />

            {/* Grants */}
            <Route path="/grants" element={<GrantsIndex />} />
            <Route path="/grants/:slug" element={<GrantDetailPage />} />

            {/* Guides */}
            <Route path="/guides" element={<GuidesIndex />} />
            <Route path="/guides/:slug" element={<GuideDetailPage />} />

            {/* Crops */}
            <Route path="/crops" element={<CropsIndex />} />
            <Route path="/crops/:slug" element={<CropDetailPage />} />

            {/* State SEO Pages */}
            <Route path="/agricultural-land-rental-rates-2025" element={<AgriculturalLandRentalRates2025 />} />
            <Route path="/farmland-rental-rates-:stateSlug" element={<StatePage />} />

            {/* Learning Center - Long-tail SEO */}
            <Route path="/learn" element={<LearningCenter />} />
            <Route path="/learn/:slug" element={<LongTailContent />} />

            {/* Ad Landing Pages */}
            <Route path="/try/calculator" element={<TryCalculator />} />
            <Route path="/try/grants" element={<TryGrants />} />
            <Route path="/try/ai-advisor" element={<TryAIAdvisor />} />

            {/* Legal Pages */}
            <Route path="/privacy-policy" element={<LegalPage />} />
            <Route path="/terms-of-service" element={<LegalPage />} />
            <Route path="/cookie-policy" element={<LegalPage />} />
            <Route path="/data-security" element={<LegalPage />} />

            {/* 404 Route */}
            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
